import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import {
  WEB_COMPONENT_GZ_CHIPS_GENERATOR_INPUT,
  WEB_COMPONENT_MOBILE_SECURITY_ACTIVATION_EMAILS_DIALOG_BODY,
} from '../../../../webcomponents.constants';
import { Events } from '../../../../ui/gzInfoBox/types';
import { WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { FontSizes } from '../../../../types/common/types';
import { LinkColor, LinkType } from '../../../../ui/gzLinkComponent/types';
import { GzChipsGeneratorInputWebComponent } from '../../../../ui/gzChipsGeneratorInput';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { ACTIVATION_EMAIL_DIALOG_LEARN_MORE_URL, EMAILS_CHANGE_EVENT } from '../../../common/constants';

/**
 * @summary This component is used solely inside the Mobile Security Activation Emails Dialog and contains the body of the dialog.
 * @tag gz-mobile-security-activation-emails-dialog-body
 */
@customElement(WEB_COMPONENT_MOBILE_SECURITY_ACTIVATION_EMAILS_DIALOG_BODY)
export class MobileSecurityActivationEmailsDialogBodyWebComponent extends LitElement {
  private translations: any;

  static get styles() {
    return styles;
  }

  constructor() {
    super();
    this.translations = BaseWebComponentTranslator.getInstance().getMobileSecurityStrings();

    this.addEventListener(Events.INFO_BOX_UPDATED, (event: WebComponentEvent) => {
      const infoBoxContainer = event.detail.infoBox.shadowRoot.querySelector('.info-box-container');
      infoBoxContainer.style.margin = '20px 0 0 0';
    });
  }

  /**
   * Arrays of strings. Each string represents an email given by the user to which the link will be sent.
   */
  get emails(): Array<string> {
    const chipsGenerator = this.shadowRoot?.querySelector(
      WEB_COMPONENT_GZ_CHIPS_GENERATOR_INPUT
    ) as GzChipsGeneratorInputWebComponent;

    return chipsGenerator.getFormValue();
  }

  render() {
    const validationRegex = '^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,63}$';

    return html`
      <div id="activation-emails-dialog-body">
        <gz-chips-generator-input
          validationRegex="${validationRegex}"
          hasFloatingLabel
          showMaxNrOfItems
          isCaseInsensitive
          .strings="${{
            placeholder: this.translations.activationEmails.placeholder,
            label: this.translations.activationEmails.emailsInputLabel,
            invalidItemErrorMessage: this.translations.activationEmails.invalidEmailMessage,
            limitReachedErrorMessage: this.translations.activationEmails.limitReached,
          }}"
        ></gz-chips-generator-input>
        <gz-info-box id="mobile-security-activation-emails-info-box">
          ${this.translations.activationEmails.info}
          <gz-link
            font-size="${FontSizes.PARAGRAPH_12}"
            type="${LinkType.UNDERLINE}"
            color="${LinkColor.SECONDARY}"
            href="${ACTIVATION_EMAIL_DIALOG_LEARN_MORE_URL}"
          >
            ${this.translations.activationEmails.learnMore}
          </gz-link>
        </gz-info-box>
      </div>
    `;
  }

  updated() {
    this.updateChipsGeneratorInput();
  }

  private updateChipsGeneratorInput() {
    const chipsGenerator = this.shadowRoot?.querySelector(
      WEB_COMPONENT_GZ_CHIPS_GENERATOR_INPUT
    ) as GzChipsGeneratorInputWebComponent;

    chipsGenerator.addEventListener('change', (event: WebComponentEvent) => {
      this.dispatchEvent(
        new CustomEvent(EMAILS_CHANGE_EVENT, {
          detail: {
            emails: event.detail.value,
          },
          bubbles: true,
          composed: true,
        })
      );
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [WEB_COMPONENT_MOBILE_SECURITY_ACTIVATION_EMAILS_DIALOG_BODY]: MobileSecurityActivationEmailsDialogBodyWebComponent;
  }
}
